// Supabase-mediated WebRTC signaling — supabase provider.
//
// Schema (Postgres): signals(id uuid pk, code, from_peer, to_peer, type,
// payload jsonb, created_at) with a (code, to_peer) index. One row per
// signaling envelope (offer / answer / ice). Lifecycle: caller inserts;
// recipient subscribes filtered by to_peer; recipient calls deleteSignal()
// after consuming. So the table stays bounded modulo handshake bursts.
//
// This is NOT a full-collection snapshot (contract snapshot-semantics
// exception): subscribeIncoming delivers ONE envelope at a time, so there is
// no maintainCollection cache here — we pass INSERTs straight through and do a
// one-shot select of any rows already waiting.
//
// INTENTIONAL OMISSION — no rate limiter. The Firestore signaling.js wrapped
// every write in a per-code token bucket (20 writes / 10 s, queue + overflow).
// That limiter existed solely to dodge Firestore's daily free-tier write quota
// — the very limit Supabase removes. Replicating it here would throttle the
// WebRTC handshake for no benefit, so it is deliberately dropped. (If
// Supabase-side abuse protection is ever wanted, it belongs in RLS / an edge
// function, not a client token bucket.)
import { getClient } from './client.js';

// Unique suffix per subscribeIncoming() call. supabase-js keys channels by
// topic; a fixed name (e.g. `signals:to=X`) collides when the same peer
// re-subscribes (StrictMode dev double-mount, or a partyCode/user effect
// re-run) — the second `.channel()` returns the first's already-subscribed
// instance and `.on(...)` throws "cannot add postgres_changes callbacks after
// subscribe()", which kills signaling so the WebRTC handshake never completes.
let signalChannelSeq = 0;

// How often to re-poll for envelopes realtime may have dropped. The WebRTC
// handshake is one-shot and timing-critical: an offer/answer inserted in the
// window before a freshly-subscribed channel's postgres_changes binding is
// ready is silently missed by realtime. Consumed rows are deleted, so a poll
// only ever returns un-consumed (missed or brand-new) envelopes.
const SIGNAL_POLL_MS = 1500;

/**
 * Send a signaling envelope (offer / answer / ICE candidate). Validates the
 * same fields the Firestore version did (throws TypeError on a missing code or
 * from/to/type), then inserts a row. No client-side rate limiting (see the
 * module header).
 *
 * @param {string} code  partyCode
 * @param {{from:string, to:string, type:'offer'|'answer'|'ice', payload:object}} sig
 * @returns {Promise<void>}
 */
export async function sendSignal(code, sig) {
  if (!code) throw new TypeError('sendSignal: code is required');
  if (!sig || !sig.from || !sig.to || !sig.type) {
    throw new TypeError('sendSignal: from/to/type required');
  }
  await getClient()
    .from('signals')
    .insert({
      code,
      from_peer: String(sig.from),
      to_peer: String(sig.to),
      type: String(sig.type),
      payload: sig.payload ?? {},
      // created_at omitted → DB default now()
    });
}

/**
 * Subscribe to incoming signals addressed to `myPeerId`. The callback receives
 * ONE IncomingSignal at a time; the caller MUST call deleteSignal(code, id)
 * after consuming each. NOT a full-collection snapshot. Returns the idempotent
 * unsubscribe fn. Returns a no-op if code/myPeerId is missing.
 *
 * Backfill: an initial select fires onSignal for any rows already waiting for
 * (code, to_peer) — covers envelopes inserted before the realtime channel was
 * established. The realtime channel then delivers each subsequent INSERT.
 *
 * @param {string} code
 * @param {string} myPeerId
 * @param {(sig: {id:string, from:string, to:string, type:string, payload:object}) => void} onSignal
 * @returns {() => void}  unsubscribe
 */
export function subscribeIncoming(code, myPeerId, onSignal) {
  if (!code || !myPeerId) return () => {};
  const supabase = getClient();
  let closed = false;
  // Dedup so the realtime delivery and the safety-net poll (which can both see
  // the same row in the window before the recipient deletes it) only fire
  // onSignal once per envelope.
  const seen = new Set();

  const toIncoming = (row) => ({
    id: row.id,
    from: row.from_peer,
    to: row.to_peer,
    type: row.type,
    payload: row.payload || {},
  });

  const deliver = (row) => {
    if (closed || !row || row.code !== code) return;
    if (seen.has(row.id)) return;
    seen.add(row.id);
    onSignal(toIncoming(row));
  };

  // 1. realtime: INSERTs addressed to me. The filter narrows to to_peer at the
  //    server; we also client-check row.code === code since the filter can
  //    only express one column condition. Unique channel name avoids the
  //    re-subscribe collision (see signalChannelSeq above).
  signalChannelSeq += 1;
  const channel = supabase
    .channel(`signals:to=${myPeerId}:${signalChannelSeq}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'signals',
        filter: `to_peer=eq.${myPeerId}`,
      },
      (payload) => deliver(payload.new),
    )
    .subscribe();

  // 2. backfill + safety-net poll. A single select can't cover envelopes
  //    inserted during the subscribe→binding-ready window, so we re-select on
  //    an interval. `seen` dedups against realtime; consumed rows are deleted
  //    by the recipient, so the poll naturally drains.
  const pollOnce = () => {
    supabase
      .from('signals')
      .select('*')
      .eq('code', code)
      .eq('to_peer', myPeerId)
      .then(({ data, error }) => {
        if (closed || error || !data) return;
        data.forEach(deliver);
      });
  };
  pollOnce();
  const pollId = setInterval(pollOnce, SIGNAL_POLL_MS);

  return () => {
    if (closed) return;
    closed = true;
    clearInterval(pollId);
    supabase.removeChannel(channel);
  };
}

/**
 * Delete a consumed signal row. Best-effort, swallowed.
 *
 * @param {string} code
 * @param {string} signalId
 * @returns {Promise<void>}
 */
export async function deleteSignal(code, signalId) {
  if (!code || !signalId) return;
  try {
    await getClient().from('signals').delete().eq('id', signalId);
  } catch {
    /* best-effort */
  }
}

// Parity shims so the supabase barrel's re-export surface can match firebase's
// signaling module if the selector re-exports the whole namespace. The
// Firestore version exposed these for its token-bucket tests; there is no
// bucket here (see module header), so they are inert.
export function __resetBuckets() {
  /* no-op — supabase signaling has no rate-limit buckets */
}
export const __test__ = {};
